import lodash from 'lodash';
import moment from 'moment';

import Query, { Operators, ValueSets } from '@complion/query';
const { ValueSetRegistry } = Query;

import {
    ALLOWED_SORTING_DIRECTIONS,
    ALLOWED_SORTING_SYSTEM_PROPERTIES,
    ALLOWED_SORTING_TYPES,
} from 'common/enums/folderSorting';
import parse from 'csv-parse/lib/es5/sync';
import uuid from 'node-uuid';

const { Conditions, DocumentType, Descriptor } = Query.Nodes;
const { FixedValue, ScalarValue, DataSourceSingleValue, DataSourceMultipleValue, DataSourceValueList } = ValueSets;

export default class BinderTemplateCsvLoader {
    constructor({ institutionId, documentTypes, descriptors, dataSources }) {
        this.institutionId = institutionId;
        this.documentTypes = documentTypes;
        this.descriptors = descriptors;
        this.dataSources = dataSources;
        this.errors = [];
    }

    getDocumentType({ rowNum, documentTypeDisplayName }) {
        const documentType = this.documentTypes.find((dt) => lodash.trim(dt.displayName) === documentTypeDisplayName);
        if (!documentType) {
            this.errors.push(`${rowNum}: document type '${documentTypeDisplayName}' not found.`);
            return;
        }
        return documentType;
    }

    getDescriptor({ rowNum, documentType, descriptorDisplayName }) {
        if (documentType) {
            const descriptor = documentType.descriptors.find(
                (des) => lodash.trim(des.displayName) === descriptorDisplayName
            );
            if (!descriptor) {
                this.errors.push(
                    `${rowNum}: descriptor '${descriptorDisplayName}' not linked to '${documentType.displayName}' document type.`
                );
                return;
            }
            return this.descriptors.find((d) => d.id === descriptor.id);
        } else {
            const descriptor = this.descriptors.find((des) => lodash.trim(des.displayName) === descriptorDisplayName);
            if (!descriptor) {
                this.errors.push(`${rowNum}: descriptor '${descriptorDisplayName}' not found.`);
                return;
            }
            return descriptor;
        }
    }

    getDataSource({ rowNum, dataSourceName }) {
        const datasource = this.dataSources.find((ds) => lodash.trim(ds.name) === dataSourceName);
        if (!datasource) {
            this.errors.push(`${rowNum}: Data source '${dataSourceName}' not found.`);
            return;
        }
        return datasource;
    }

    getValueSet({ rowNum, descriptor, valueSetType, valueSetValue }) {
        const isDataset = descriptor.dataType.toLowerCase() === 'dataset';
        const allowedValueSetTypes = lodash
            .chain(Query.ValueSets)
            .filter((vs) => vs.dataset === isDataset)
            .map((vs) => vs.name)
            .value();

        if (!allowedValueSetTypes.includes(valueSetType)) {
            this.errors.push(
                `${rowNum}: Invalid value set type '${valueSetType}'. Valid values are:  ${JSON.stringify(
                    allowedValueSetTypes
                )}`
            );
            return;
        }

        const ValueSetType = ValueSetRegistry.resolve(valueSetType);
        const valueSet = new ValueSetType();
        switch (ValueSetType) {
            case DataSourceMultipleValue:
            case DataSourceSingleValue:
            case DataSourceValueList:
                {
                    const datasource = this.getDataSource({ rowNum, dataSourceName: valueSetValue });
                    if (!datasource) {
                        return;
                    }
                    valueSet.dataSourceId = datasource.id;
                }
                break;
            case FixedValue:
                valueSet.value = valueSetValue;
                break;
            case ScalarValue:
                valueSet.value = moment().utc().format('DD MMM YYYY'); // the date value is not important
                break;
            default:
                this.errors.push(`${rowNum}: Unsupported value set type ${valueSetType}`);
                return;
        }
        return valueSet;
    }

    getOperator({ rowNum, operatorName, descriptor, valueSet }) {
        const validOperators =
            Operators[Operators.getOperatorSet(descriptor.dataType, valueSet.constructor.cardinality)];
        const operator = lodash.find(validOperators, (op) => op.name === operatorName);
        if (!operator) {
            this.errors.push(
                `${rowNum}: invalid operator '${operatorName}'. Valid values are: ${JSON.stringify(
                    lodash.map(validOperators, (op) => op.name)
                )}.`
            );
            return;
        }
        return operator;
    }

    buildDescriptorPredicate({ rowNum, documentType, predicate }) {
        const { descriptorDisplayName, operatorName, valueSetType, valueSetValue } = predicate;
        const descriptor = this.getDescriptor({
            rowNum,
            documentType,
            descriptorDisplayName,
        });
        if (!descriptor) {
            return;
        }
        const valueSet = this.getValueSet({ rowNum, descriptor, valueSetType, valueSetValue });
        if (!valueSet) {
            return;
        }
        const operator = this.getOperator({ rowNum, descriptor, valueSet, operatorName });
        if (!operator) {
            return;
        }

        const descriptorPredicate = new Descriptor();
        descriptorPredicate.descriptor = descriptor;
        descriptorPredicate.value = valueSet;
        descriptorPredicate.operator = operator.value;
        return descriptorPredicate;
    }

    buildDocumentTypePredicate({ rowNum, documentTypeDisplayName, predicates }) {
        const documentType = this.getDocumentType({ rowNum, documentTypeDisplayName });
        if (!documentType) {
            return;
        }
        const documentTyprePredicate = new DocumentType();
        documentTyprePredicate.documentType = documentType;

        for (const predicate of predicates) {
            const descriptorPredicate = this.buildDescriptorPredicate({ rowNum, documentType, predicate });
            if (descriptorPredicate) {
                documentTyprePredicate.addChildren(descriptorPredicate);
            }
        }

        return documentTyprePredicate;
    }

    buildQueries(queries, rootFolderConditions, binderTemplateId, folder) {
        let conditions = rootFolderConditions;
        if (folder.parent) {
            conditions = this.buildFolderConditions(folder);
        }
        const options = this.getSortOptions(folder);

        queries.push({
            id: folder.id,
            displayName: folder.displayName,
            description: folder.description,
            displayOrder: folder.displayOrder,
            parentId: folder.parent ? folder.parent.id : null,
            binderTemplateId,
            query: Query.jsonHelper.stringify(conditions),
            options,
        });
        for (const subFolder of folder.subFolders) {
            this.buildQueries(queries, rootFolderConditions, binderTemplateId, subFolder);
        }
    }

    buildFolderConditions(folder) {
        const folderConditions = new Conditions();
        this.getDocumentTypesForFolder(folder).forEach((documentTypeDisplayName) => {
            const folderDocTypePred = new DocumentType();
            folderDocTypePred.documentType = this.getDocumentType({ rowNum: folder.rowNum, documentTypeDisplayName });
            folderConditions.addChildren(folderDocTypePred);
        });
        return folderConditions;
    }

    getSortOptions(folder) {
        const options = {
            sort: [],
            customSort: [],
        };
        if (folder.sort) {
            for (const sortExpression of folder.sort) {
                const { sortDescriptorDisplayName: descriptorDisplayName, direction, sortType } = sortExpression;
                const rowNum = folder.rowNum;
                const folderDocumentTypeDisplayNames = this.getDocumentTypesForFolder(folder);
                const folderDocumentTypes = this.documentTypes.filter((dt) =>
                    folderDocumentTypeDisplayNames.includes(lodash.trim(dt.displayName))
                );

                let subType;
                if (sortType === ALLOWED_SORTING_TYPES.descriptor.value) {
                    const sortDescriptor = this.getDescriptor({ rowNum, descriptorDisplayName });
                    if (!sortDescriptor) {
                        return;
                    }
                    const descriptorId = sortDescriptor.id;
                    if (
                        !lodash.every(folderDocumentTypes, (documentType) =>
                            lodash.some(documentType.descriptors, { id: descriptorId })
                        )
                    ) {
                        const unlinkedDocumentTypeDisplayNames = folderDocumentTypes
                            .filter((documentType) => !lodash.some(documentType.descriptors, { id: descriptorId }))
                            .map((dt) => dt.displayName);
                        this.errors.push(
                            `${rowNum}: Sort descriptor '${descriptorDisplayName}' is not linked to document types ${JSON.stringify(
                                unlinkedDocumentTypeDisplayNames
                            )} in '${folder.displayName}' folder.`
                        );
                        return;
                    }
                    subType = descriptorId;
                } else {
                    subType = Object.keys(ALLOWED_SORTING_SYSTEM_PROPERTIES).find(
                        (subType) => ALLOWED_SORTING_SYSTEM_PROPERTIES[subType].name === descriptorDisplayName
                    );
                }

                options.sort.push({
                    subType,
                    sortType,
                    direction,
                });
            }
        }
        return options;
    }

    load(csv) {
        try {
            const binderTemplateId = uuid.v4();
            const { nodes, rootFolder } = this.processCsv(csv);
            if (nodes && rootFolder) {
                const allConditions = new Conditions();
                allConditions.addChildren(nodes.map((node) => this.buildDocumentTypePredicate(node)).filter((p) => p));

                const queries = [];
                this.buildQueries(queries, allConditions, binderTemplateId, rootFolder);

                const linkedDocumentTypes = this.getDocumentTypesForFolder(rootFolder);
                const binderTemplate = {
                    id: binderTemplateId,
                    displayName: '',
                    description: '',
                    status: 'NEW',
                    documentTypes: this.documentTypes.filter((dt) => linkedDocumentTypes.includes(dt.displayName)),
                    queries: queries,
                    binderType: 'Regulatory',
                    billable: true,
                    eventsTabEnabled: false,
                };
                return { binderTemplate, errors: this.errors };
            } else {
                return { errors: this.errors };
            }
        } catch (error) {
            this.errors.push(`Unhandled Error: ${error.message}`);
            return { errors: this.errors };
        }
    }

    validateCsv(csv) {
        let records;
        try {
            records = parse(csv, {
                columns: (header) => header.map(lodash.camelCase),
            });
        } catch (error) {
            this.errors.push(`Unable to parse CSV file: '${error.message}'`);
            return;
        }

        if (records.length < 3) {
            this.errors.push('The CSV file should containt more than two records');
            return;
        }

        const requiredCsvColumns = {
            folder: 'Folder',
            folderSort: 'Folder Sort',
            documentTypes: 'Document Types',
            documentTypeConditions: 'Document Type Conditions',
            operator: 'Operator',
            dataType: 'Data Type',
            datalink: 'Datalink',
        };

        const columns = Object.keys(records[0]).filter((key) => !key.match(/^folder\d$/));
        const missingColumns = lodash
            .difference(Object.keys(requiredCsvColumns), columns)
            .map((key) => requiredCsvColumns[key]);
        if (!lodash.isEmpty(missingColumns)) {
            this.errors.push(`Missing required column(s) in CSV file: ${JSON.stringify(missingColumns)}`);
            return;
        }
        return records;
    }

    processCsv(csv) {
        const records = this.validateCsv(csv);
        if (lodash.isEmpty(records)) {
            return {};
        }

        const nodes = [];
        const rootFolder = {
            id: uuid.v4(),
            parent: null,
            subFolders: [],
            documentTypes: [],
            displayName: 'All Documents',
            description: 'All Documents',
            displayOrder: 0,
            level: 0,
            rowNum: 3, // header, Unfiled Documents (System Folder), All Documents
        };
        let currentFolder = rootFolder;
        for (let rowNum = 0; rowNum < records.length; rowNum++) {
            const record = records[rowNum];

            let {
                documentTypes: rawDocumentType,
                documentTypeConditions: rawDescriptorName,
                operator: rawOperator,
                dataType: rawDataType,
                datalink: rawValue,
            } = record;

            rawDocumentType = lodash.trim(rawDocumentType);
            rawDescriptorName = lodash.trim(rawDescriptorName);
            rawOperator = lodash.trim(rawOperator);
            rawDataType = lodash.trim(rawDataType);
            rawValue = lodash.trim(rawValue);

            if (rowNum >= 2) {
                currentFolder = this.parseFolders(record, currentFolder, rowNum + 2);
            }

            if (!lodash.isEmpty(rawDescriptorName)) {
                const predicate = {
                    descriptorDisplayName: rawDescriptorName,
                    operatorName: rawOperator,
                    valueSetType: rawDataType,
                    valueSetValue: rawValue,
                };

                if (lodash.isEmpty(rawDocumentType)) {
                    nodes[nodes.length - 1].predicates.push(predicate);
                } else {
                    const node = {
                        documentTypeDisplayName: rawDocumentType,
                        predicates: [predicate],
                        rowNum: rowNum + 2,
                    };
                    nodes.push(node);
                    currentFolder.documentTypes.push(rawDocumentType);
                }
            }
        }
        if (currentFolder.subFolders.length === 0 && currentFolder.documentTypes.length === 0) {
            this.errors.push(`${currentFolder.rowNum}: Empty folder '${currentFolder.displayName}'`);
        }

        return { nodes, rootFolder };
    }

    parseFolders(record, currentFolder, rowNum) {
        const folders = Object.keys(record)
            .filter((key) => key.match(/^folder\d?$/))
            .sort()
            .map((key) => record[key]);
        for (let level = 1; level <= folders.length; level++) {
            const folder = lodash.trim(folders[level - 1]);

            if (!lodash.isEmpty(folder)) {
                let invalidFolderStructure = false;
                // parent folder is not specfied
                if (level - currentFolder.level > 1) {
                    invalidFolderStructure = true;
                }
                let parent = currentFolder;

                for (let i = level; currentFolder.level >= i; i++) {
                    // should never happen (defensive proragmming)
                    if (!parent || !parent.parent) {
                        invalidFolderStructure = true;
                        break;
                    }
                    parent = parent.parent;
                }
                if (invalidFolderStructure) {
                    this.errors.push(
                        `${rowNum}: Invalid folder structure: new folder='${folder}', previous folder='${currentFolder.displayName}'`
                    );
                    continue;
                }
                let sort = this.parseFolderSort(rowNum, record['folderSort']);
                if (!sort && parent.sort) {
                    sort = parent.sort;
                }
                const newFolder = {
                    id: uuid.v4(),
                    parent,
                    displayName: folder,
                    description: folder,
                    displayOrder: (parent.subFolders.length + 1) * 1000,
                    documentTypes: [],
                    subFolders: [],
                    level,
                    sort,
                    rowNum,
                };
                parent.subFolders.push(newFolder);
                if (folder.length < 2 || folder.length > 80) {
                    this.errors.push(
                        `${rowNum}: The folder name must be a minimum of 2 characters and a maximum of 80 character`
                    );
                }
                if (currentFolder.subFolders.length === 0 && currentFolder.documentTypes.length === 0) {
                    this.errors.push(`${currentFolder.rowNum}: Empty folder '${currentFolder.displayName}'`);
                }
                currentFolder = newFolder;
            }
        }
        return currentFolder;
    }

    parseFolderSort(rowNum, folderSorts) {
        if (lodash.trim(folderSorts).length === 0) {
            return;
        }
        folderSorts = lodash.trim(folderSorts);
        const sort = [];
        for (let folderSort of folderSorts.split(',')) {
            folderSort = lodash.trim(folderSort);
            if (lodash.isEmpty(folderSort)) {
                continue;
            }
            const lastIndexOfSpace = folderSort.lastIndexOf(' ');
            const direction = lodash.trim(folderSort.substring(lastIndexOfSpace + 1).toLowerCase());
            const sortDescriptorDisplayName = lodash.trim(folderSort.substring(0, lastIndexOfSpace));
            let sortType = ALLOWED_SORTING_TYPES.descriptor.value;
            if (lodash.map(ALLOWED_SORTING_SYSTEM_PROPERTIES, (sp) => sp.name).includes(sortDescriptorDisplayName)) {
                sortType = ALLOWED_SORTING_TYPES.systemProperty.value;
            }

            if (!Object.keys(ALLOWED_SORTING_DIRECTIONS).includes(direction)) {
                this.errors.push(
                    `row: ${rowNum}, Invalid value for sort direction '${direction}'. Valid values are:  ${JSON.stringify(
                        Object.keys(ALLOWED_SORTING_DIRECTIONS)
                    )}`
                );
                return;
            }
            sort.push({
                sortType,
                direction,
                sortDescriptorDisplayName,
            });
        }
        return sort;
    }

    getDocumentTypesForFolder(folder) {
        const folderDocumentTypes = [];
        folderDocumentTypes.push(...folder.documentTypes);
        for (const subFolder of folder.subFolders) {
            folderDocumentTypes.push(...this.getDocumentTypesForFolder(subFolder));
        }
        return folderDocumentTypes;
    }
}
